<!--
  FrameX "Not in FrameX" section: a collapsed, muted, sheet-grouped list of
  frame placements on non-working sheets (`framesNotInFrameX`). Placement-based
  like the list above — selection routes through `selectedLocations`, so rows
  select both ways with the canvas. Each sheet group has an "Add sheet to
  FrameX" recovery. Expand state is a module store (sticky across tab switches).
-->
<script>
  import { createEventDispatcher } from "svelte";

  import { rangeBetween } from "../markers.js";
  import { selectAllIds } from "../selection.js";
  import { selectedLocations } from "#src/stores/ui.js";
  import { notInFrameXExpanded } from "../panel-state.js";
  import CaretDownIcon from "@local/assets/icons/caret-down.svg";
  import FrameMarkerRow from "./FrameMarkerRow.svelte";

  export let group;
  /** Orphan placement locations, in document order (`framesNotInFrameX`). */
  export let frames = [];
  /** `{ pageId -> page }`, for a sheet group's label. */
  export let pages;
  /** `{ pageId -> zero-based visible index }`, for the "Page P" fallback. */
  export let visibleIndices;
  export let settings;

  const dispatch = createEventDispatcher();

  // The flat, top-to-bottom id order the shift-range is measured across. The
  // groups below preserve `frames`' document order, so this matches the
  // visible order exactly.
  $: orphanIds = (frames || []).map((l) => l.id);

  // Group by sheet, preserving document order (first appearance wins).
  $: groups = groupByPage(frames);
  function groupByPage(locs) {
    const out = [];
    const byId = new Map();
    for (const loc of locs || []) {
      let g = byId.get(loc.page_id);
      if (!g) {
        g = {
          pageId: loc.page_id,
          label: pages?.[loc.page_id]?.label || `Page ${(visibleIndices?.[loc.page_id] ?? 0) + 1}`,
          frames: [],
        };
        byId.set(loc.page_id, g);
        out.push(g);
      }
      g.frames.push(loc);
    }
    return out;
  }

  // A canvas selection landing on an orphan reveals it — the section opens so
  // the highlighted row isn't hidden inside a collapsed block.
  $: if ((frames || []).some((l) => $selectedLocations[l.id])) $notInFrameXExpanded = true;

  // Own the shared selection, exactly as FrameMarkersList.selectRow does, but
  // over this section's flat id order so shift-ranges stay within it.
  let anchor = null;
  function selectRow(event) {
    const { loc, ctrlKey, metaKey, shiftKey } = event.detail;

    if (shiftKey && anchor != null) {
      selectedLocations.set(selectAllIds(rangeBetween(orphanIds, anchor, loc.id)));
      return;
    }

    if (ctrlKey || metaKey) {
      selectedLocations.toggle(loc.id);
      anchor = loc.id;
      return;
    }

    selectedLocations.selectOnly(loc.id);
    anchor = loc.id;
  }
</script>

{#if (frames || []).length > 0}
  <div class="fx-orphans">
    <button
      type="button"
      class="fx-orphans-head"
      on:click={() => ($notInFrameXExpanded = !$notInFrameXExpanded)}>
      <span class="fx-orphans-chevron" class:open={$notInFrameXExpanded}>
        <CaretDownIcon />
      </span>
      <span class="fx-orphans-title">Not in FrameX ({frames.length})</span>
    </button>

    {#if $notInFrameXExpanded}
      <div class="fx-orphans-body">
        {#each groups as g (g.pageId)}
          <div class="fx-orphans-group">
            <span class="fx-orphans-group-label">{g.label}</span>
            <button
              type="button"
              class="fx-orphans-add"
              title="Set this sheet back into FrameX"
              on:click|stopPropagation={() => dispatch("add-sheet", { page: g.pageId })}>
              Add sheet to FrameX
            </button>
          </div>
          {#each g.frames as loc (loc.id)}
            <FrameMarkerRow
              orphan
              {loc}
              item={$group.items[loc.record_id]}
              {settings}
              selected={Boolean($selectedLocations[loc.id])}
              on:select={selectRow}
              on:zoom-to-page
              on:hover
              on:unhover />
          {/each}
        {/each}
      </div>
    {/if}
  </div>
{/if}

<style lang="scss">
  // Sits at the bottom of the scrollable Frames list, set off by a rule and a
  // muted, uppercase heading so it reads as a secondary, recoverable list.
  .fx-orphans {
    flex: none;
    border-top: 1px solid var(--fx-border-light);
    margin-top: 4px;
  }

  .fx-orphans-head {
    all: unset;
    box-sizing: border-box;
    display: flex;
    align-items: center;
    gap: 6px;
    width: 100%;
    padding: 6px 16px;
    cursor: pointer;
    color: var(--fx-neutral);

    &:hover {
      background: #f7f8fa;
    }
  }

  .fx-orphans-chevron {
    display: inline-flex;
    flex: none;
    // The caret ships pointing down — that's the expanded state — so it's the
    // collapsed state that carries the rotation.
    transform: rotate(-90deg);
    transition: transform 0.12s ease;

    &.open {
      transform: rotate(0deg);
    }

    :global(svg) {
      display: block;
      width: 12px;
      height: 12px;
    }
  }

  .fx-orphans-title {
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.04em;
    text-transform: uppercase;
  }

  .fx-orphans-body {
    display: flex;
    flex-direction: column;
    padding-bottom: 4px;
  }

  // Per-sheet subheader: the muted sheet label on the left, the accent-link
  // recovery action on the right.
  .fx-orphans-group {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 8px;
    padding: 5px 16px 3px;
  }

  .fx-orphans-group-label {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.02em;
    text-transform: uppercase;
    color: var(--fx-text-secondary);
  }

  .fx-orphans-add {
    all: unset;
    flex: none;
    cursor: pointer;
    font-size: 11px;
    font-weight: 700;
    color: var(--fx-accent);

    &:hover {
      text-decoration: underline;
    }
  }
</style>
